// src/services/notificationService.js
import { supabase } from '../lib/supabase';
import { resolveNotificationCaseId } from '../lib/resolveNotificationCaseId';

/**
 * Notification Service
 * Handles all notification-related operations with Supabase
 */
class NotificationService {
  /**
   * Get all notifications for a user
   * @param {string} userId - User ID
   * @param {boolean} unreadOnly - If true, only return unread notifications
   * @returns {Promise<Array>} Array of notifications
   */
async getUserNotifications(userId, unreadOnly = false) {
  try {
    // 1. Fetch from simple 'notifications' table
    let simpleQuery = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
 
    if (unreadOnly) {
      simpleQuery = simpleQuery.eq('is_read', false);
    }
 
    const { data: simpleNotifications, error: simpleError } = await simpleQuery;
 
    if (simpleError) {
      console.error('Error fetching simple notifications:', simpleError);
    }
 
    // 2. Fetch from 'dashboard_notifications' table
    let dashboardQuery = supabase
      .from('dashboard_notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
 
    if (unreadOnly) {
      dashboardQuery = dashboardQuery.eq('is_read', false);
    }
 
    const { data: dashboardNotifications, error: dashboardError } = await dashboardQuery;
 
    if (dashboardError) {
      console.error('Error fetching dashboard notifications:', dashboardError);
    }
 
    // 3. Normalize both types to a common format
    const normalizedSimple = (simpleNotifications || []).map(notif => ({
      id: notif.id,
      user_id: notif.user_id,
      type: notif.type, // 'info', 'warning', 'urgent', 'success', 'error'
      title: notif.title,
      message: notif.message,
      link: notif.link,
      is_read: notif.is_read,
      created_at: notif.created_at,
      read_at: notif.read_at,
      metadata: notif.metadata || {},
      // Add source identifier
      source: 'simple',
      // Map fields for consistency
      action_url: notif.link,
      action_label: null,
      priority: notif.type === 'urgent' ? 10 : notif.type === 'warning' ? 7 : 5,
      invoice_id: notif.metadata?.invoice_id || null,
      case_id: notif.case_id || notif.metadata?.caseId || notif.metadata?.case_id || null,
      is_dismissed: false
    }));
 
    const normalizedDashboard = (dashboardNotifications || []).map(notif => ({
      id: notif.id,
      user_id: notif.user_id,
      type: notif.type, // 'payment_reminder', etc.
      title: notif.title,
      message: notif.message,
      link: notif.action_url,
      is_read: notif.is_read,
      created_at: notif.created_at,
      read_at: notif.read_at,
      metadata: {},
      // Add source identifier
      source: 'dashboard',
      // Dashboard-specific fields
      action_url: notif.action_url,
      action_label: notif.action_label,
      priority: notif.priority || 5,
      invoice_id: notif.invoice_id || null,
      case_id: notif.case_id || null,
      is_dismissed: notif.is_dismissed || false,
      dismissed_at: notif.dismissed_at || null,
      expires_at: notif.expires_at || null
    }));
 
    // 4. Merge and sort by created_at (most recent first)
    const allNotifications = [...normalizedSimple, ...normalizedDashboard]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 50); // Keep only top 50
 
    console.log(`📬 Fetched ${simpleNotifications?.length || 0} simple + ${dashboardNotifications?.length || 0} dashboard = ${allNotifications.length} total notifications`);
 
    return allNotifications;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }
}
 

  /**
   * Get unread notification count for a user
   * @param {string} userId - User ID
   * @returns {Promise<number>} Count of unread notifications
   */
  async getUnreadCount(userId) {
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  }

  /**
   * Get all administrator user IDs
   * @returns {Promise<Array<string>>} Array of admin user IDs
   */
  async getAdministratorUserIds() {
    try {
      const { data, error } = await supabase
        .from('user_role_assignments')
        .select(`
          user_id,
          role:user_roles!inner(role_type)
        `)
        .eq('is_active', true)
        .eq('role.role_type', 'administrator');

      if (error) throw error;
      return (data || []).map(item => item.user_id);
    } catch (error) {
      console.error('Error fetching administrators:', error);
      return [];
    }
  }

  /**
   * Create a new notification
   * @param {Object} notification - Notification data
   * @param {string} notification.userId - User ID to send notification to
   * @param {string} notification.type - Notification type (info, warning, urgent, success, error)
   * @param {string} notification.title - Notification title
   * @param {string} notification.message - Notification message
   * @param {string} notification.link - Optional link to navigate to
   * @param {Object} notification.metadata - Optional metadata
   * @returns {Promise<Object>} Created notification
   */
  async createNotification({
    userId,
    type = 'info',
    title,
    message,
    link = null,
    metadata = {},
    caseId = null,
  }) {
    try {
      const resolvedCaseId = resolveNotificationCaseId({ caseId, metadata, link });
      const resolvedMetadata = {
        ...(metadata || {}),
        ...(resolvedCaseId ? { caseId: resolvedCaseId } : {}),
      };

      const { data, error } = await supabase
        .from('notifications')
        .insert([
          {
            user_id: userId,
            type,
            title,
            message,
            link,
            metadata: resolvedMetadata,
            case_id: resolvedCaseId,
            is_read: false,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  /**
   * Create notifications for multiple users
   * @param {Array<string>} userIds - Array of user IDs
   * @param {Object} notification - Notification data
   * @returns {Promise<Array>} Created notifications
   */
  async createBulkNotifications(
    userIds,
    { type = 'info', title, message, link = null, metadata = {}, caseId = null }
  ) {
    try {
      const resolvedCaseId = resolveNotificationCaseId({ caseId, metadata, link });
      const resolvedMetadata = {
        ...(metadata || {}),
        ...(resolvedCaseId ? { caseId: resolvedCaseId } : {}),
      };

      const notifications = userIds.map((userId) => ({
        user_id: userId,
        type,
        title,
        message,
        link,
        metadata: resolvedMetadata,
        case_id: resolvedCaseId,
        is_read: false,
      }));

      const { data, error } = await supabase
        .from('notifications')
        .insert(notifications)
        .select();

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error creating bulk notifications:', error);
      throw error;
    }
  }

  /**
   * Notify all administrators
   * @param {Object} notification - Notification data
   * @returns {Promise<Array>} Created notifications
   */
  async notifyAllAdministrators({
    type = 'info',
    title,
    message,
    link = null,
    metadata = {},
    caseId = null,
    /** N’envoie pas de notification à ces utilisateurs (ex. auteur de l’action). */
    excludeUserIds = [],
  }) {
    try {
      const exclude = new Set(
        (excludeUserIds || []).filter(Boolean).map(String)
      );
      let adminIds = (await this.getAdministratorUserIds()).filter(
        (id) => id && !exclude.has(String(id))
      );

      if (adminIds.length === 0) {
        console.warn('No administrators found to notify');
        return [];
      }

      return await this.createBulkNotifications(adminIds, {
        type,
        title,
        message,
        link,
        metadata,
        caseId,
      });
    } catch (error) {
      console.error('Error notifying administrators:', error);
      throw error;
    }
  }

  /**
   * Mark a notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} Updated notification
   */
  async markAsRead(notificationId) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read for a user
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  async markAllAsRead(userId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Marque comme lues les notifications liées à un dossier (messages, etc.).
   */
  async markCaseNotificationsAsRead(userId, caseId) {
    if (!userId || !caseId) return;

    try {
      const caseLink = `/case-detail/${caseId}`;

      const { error: linkError } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false)
        .eq('link', caseLink);

      if (linkError) throw linkError;

      const { error: metaError } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false)
        .contains('metadata', { caseId });

      if (metaError) throw metaError;
    } catch (error) {
      console.error('Error marking case notifications as read:', error);
      throw error;
    }
  }

  /**
   * Delete a notification
   * @param {string} notificationId - Notification ID
   * @returns {Promise<void>}
   */
  async deleteNotification(notificationId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Delete all read notifications for a user
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  async deleteAllRead(userId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId)
        .eq('is_read', true);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting read notifications:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time notifications for a user
   * @param {string} userId - User ID
   * @param {Function} callback - Callback function to handle new notifications
   * @returns {Object} Subscription object with unsubscribe method
   */
  subscribeToNotifications(userId, callback) {
    const subscription = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          callback(payload.new);
        }
      )
      .subscribe();

    return {
      unsubscribe: () => {
        subscription.unsubscribe();
      },
    };
  }

  // ============================================================================
  // HELPER METHODS FOR COMMON NOTIFICATION SCENARIOS
  // ============================================================================

  /**
   * Send case status change notification
   * Notifies the assigned user and all administrators
   */
  async notifyCaseStatusChange(userId, caseNumber, oldStatus, newStatus, caseId = null) {
    const notification = {
      type: 'info',
      title: 'Mise à jour du statut du cas',
      message: `Le cas ${caseNumber} est passé de "${oldStatus}" à "${newStatus}"`,
      link: caseId ? `/case-detail/${caseId}` : `/case-detail/${caseNumber}`,
      metadata: { caseNumber, oldStatus, newStatus, caseId },
    };

    // Notify the assigned user
    await this.createNotification({
      userId,
      ...notification,
    });

    // Notify all administrators
    await this.notifyAllAdministrators(notification);
  }

  /**
   * Send urgent case notification
   * Notifies the assigned user and all administrators
   */
  async notifyUrgentCase(userId, caseNumber, reason, caseId = null) {
    const notification = {
      type: 'urgent',
      title: 'Cas urgent nécessitant une attention',
      message: `Le cas ${caseNumber} nécessite une attention immédiate: ${reason}`,
      link: caseId ? `/case-detail/${caseId}` : `/case-detail/${caseNumber}`,
      metadata: { caseNumber, reason, caseId },
    };

    // Notify the assigned user
    await this.createNotification({
      userId,
      ...notification,
    });

    // Notify all administrators
    await this.notifyAllAdministrators(notification);
  }

  /**
   * Send incident report notification
   * Notifies the assigned user and all administrators
   */
  async notifyIncident(userId, caseNumber, incidentType, caseId = null) {
    const notification = {
      type: 'warning',
      title: "Nouveau rapport d'incident",
      message: `Un incident de type "${incidentType}" a été signalé pour le cas ${caseNumber}`,
      link: caseId ? `/case-detail/${caseId}` : `/case-detail/${caseNumber}`,
      metadata: { caseNumber, incidentType, caseId },
    };

    // Notify the assigned user
    await this.createNotification({
      userId,
      ...notification,
    });

    // Notify all administrators
    await this.notifyAllAdministrators(notification);
  }

  /**
   * Send payment received notification
   * Notifies the client and all administrators
   */
  async notifyPaymentReceived(userId, invoiceNumber, amount, currency) {
    const notification = {
      type: 'success',
      title: 'Paiement reçu',
      message: `Paiement de ${amount} ${currency} reçu pour la facture ${invoiceNumber}`,
      link: `/billing-overview`,
      metadata: { invoiceNumber, amount, currency },
    };

    // Notify the client
    await this.createNotification({
      userId,
      ...notification,
    });

    // Notify all administrators
    await this.notifyAllAdministrators(notification);
  }

  /**
   * Send phase completion notification
   */
  async notifyPhaseCompletion(userId, caseNumber, phaseName, caseId = null) {
    const notification = {
      type: 'success',
      title: 'Phase terminée',
      message: `La phase "${phaseName}" du cas ${caseNumber} a été complétée`,
      link: caseId ? `/case-detail/${caseId}` : `/case-detail/${caseNumber}`,
      metadata: { caseNumber, phaseName, caseId },
    };

    // Notify the assigned user
    await this.createNotification({
      userId,
      ...notification,
    });

    // Notify all administrators
    await this.notifyAllAdministrators(notification);
  }

  /**
   * Send file upload notification
   */
  async notifyFileUploaded(userId, caseNumber, fileName, fileType, caseId = null) {
    return this.createNotification({
      userId,
      type: 'info',
      title: 'Nouveau fichier téléchargé',
      message: `Le fichier "${fileName}" (${fileType}) a été téléchargé pour le cas ${caseNumber}`,
      link: caseId ? `/case-detail/${caseId}` : `/case-detail/${caseNumber}`,
      metadata: { caseNumber, fileName, fileType, caseId },
    });
  }

  /**
   * Send message received notification
   */
  async notifyMessageReceived(userId, caseNumber, senderName, caseId = null) {
    return this.createNotification({
      userId,
      type: 'info',
      title: 'Nouveau message',
      message: `${senderName} a envoyé un message concernant le cas ${caseNumber}`,
      link: caseId ? `/case-detail/${caseId}?tab=messages` : `/case-detail/${caseNumber}`,
      metadata: { caseNumber, senderName, caseId, messageType: 'case_message' },
    });
  }

  /**
   * Notify administrators only about a new case creation
   */
  async notifyAdminsNewCase(caseNumber, dentistName, caseId = null) {
    return this.notifyAllAdministrators({
      type: 'info',
      title: 'Nouveau cas créé',
      message: `Le cas ${caseNumber} a été créé par ${dentistName}`,
      link: caseId ? `/case-detail/${caseId}` : `/cases-list`,
      metadata: { caseNumber, dentistName, caseId },
    });
  }

  /**
   * Notify administrators about system events
   */
  async notifyAdminsSystemEvent(title, message, type = 'info', link = null) {
    return this.notifyAllAdministrators({
      type,
      title,
      message,
      link,
      metadata: { systemEvent: true },
    });
  }

  /**
   * Notify administrators about user registration
   */
  async notifyAdminsNewUser(userName, userEmail) {
    return this.notifyAllAdministrators({
      type: 'info',
      title: 'Nouvel utilisateur enregistré',
      message: `${userName} (${userEmail}) vient de s'inscrire et attend l'activation.`,
      link: '/admin-users-management',
      metadata: { userName, userEmail },
    });
  }

  /**
   * Notify administrators about payment issues
   */
  async notifyAdminsPaymentIssue(invoiceNumber, clientName, reason) {
    return this.notifyAllAdministrators({
      type: 'warning',
      title: 'Problème de paiement',
      message: `Problème avec le paiement de ${clientName} pour la facture ${invoiceNumber}: ${reason}`,
      link: '/billing-overview',
      metadata: { invoiceNumber, clientName, reason },
    });
  }

  /**
   * Notify administrators and assigned staff about case assignment
   */
  async notifyCaseAssignment(assignedUserId, caseNumber, assignedBy, caseId = null) {
    const notification = {
      type: 'info',
      title: 'Nouveau cas assigné',
      message: `Le cas ${caseNumber} vous a été assigné par ${assignedBy}`,
      link: caseId ? `/case-detail/${caseId}` : `/case-detail/${caseNumber}`,
      metadata: { caseNumber, assignedBy, caseId },
    };

    // Notify the assigned user
    await this.createNotification({
      userId: assignedUserId,
      ...notification,
    });

    // Notify all administrators
    await this.notifyAllAdministrators({
      ...notification,
      message: `Le cas ${caseNumber} a été assigné par ${assignedBy}`,
    });
  }
}

export default new NotificationService();